import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, callFunction } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import type { SubscriptionRow } from "@/types/database";
import type { PlanId } from "@/lib/constants";

export function useActiveSubscription() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subscription", "active", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<SubscriptionRow | null> => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user!.id)
        .gt("expires_at", new Date().toISOString())
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useSubscriptions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subscriptions", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<SubscriptionRow[]> => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { plan: PlanId; customAmount?: number }) =>
      callFunction("subscribe", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscription"] });
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}
